import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, Building2, UserCog, Eye, Plus, Trash2, Edit, RefreshCw, ChevronRight, PenLine, Settings, Shield, KeyRound } from 'lucide-react';
import AccessDefaultsAdmin from '@/components/admin/AccessDefaultsAdmin';
import PlanConfigModal from '@/components/admin/PlanConfigModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPanel({ user, onLogout, onViewClientDashboard }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('clients');
  const [clients, setClients] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateClient, setShowCreateClient] = useState(false);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', email: '', password: '', subscription_plan: 'Growth', assigned_team_member_id: '' });
  const [newTeamMember, setNewTeamMember] = useState({ email: '', password: '' });
  const [togglingBlog, setTogglingBlog] = useState(null);
  const [planClient, setPlanClient] = useState(null);
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ name: '', email: '', password: '' });

  const handleToggleBlogAccess = async (memberId) => {
    setTogglingBlog(memberId);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/admin/team-members/${memberId}/blog-access`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error toggling blog access');
    } finally {
      setTogglingBlog(null);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [clientsRes, teamRes] = await Promise.all([
        axios.get(`${API}/admin/clients`, { headers }),
        axios.get(`${API}/admin/team-members`, { headers })
      ]);
      
      setClients(clientsRes.data);
      setTeamMembers(teamRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/clients`, newClient, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowCreateClient(false);
      setNewClient({ name: '', email: '', password: '', subscription_plan: 'Growth', assigned_team_member_id: '' });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error creating client');
    }
  };

  const handleCreateTeamMember = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/team-members`, newTeamMember, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowCreateTeam(false);
      setNewTeamMember({ email: '', password: '' });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error creating team member');
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client? All data will be lost.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error deleting client');
    }
  };

  const handleAssignClient = async (clientFirmId, teamMemberId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/assign-client`, {
        client_firm_id: clientFirmId,
        team_member_id: teamMemberId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error assigning client');
    }
  };

  const handleUpdateClientStatus = async (clientId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/admin/clients/${clientId}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error updating status');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white px-8 py-4 flex items-center justify-between" data-testid="admin-header">
        <div>
          <h1 className="text-xl font-bold" style={{fontFamily: 'Outfit'}}>Law Agency 360</h1>
          <p className="text-xs text-slate-400 mt-1">Super Admin Control Panel</p>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="text-sm font-medium">{user.email}</div>
            <div className="text-xs text-[#4338CA]">Super Admin</div>
          </div>
          <button
            onClick={onLogout}
            className="px-4 py-2 text-sm bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
            data-testid="logout-btn"
          >
            Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-8">
        {/* Page Title */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>
            Admin Control Panel
          </h2>
          <p className="text-slate-600 mt-1">Manage clients, team members, and system settings</p>
        </div>

        {/* Quick Actions */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard/blogs')}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-medium text-sm transition-all hover:shadow-lg hover:shadow-violet-500/30"
            data-testid="manage-blogs-btn"
          >
            <PenLine className="w-4 h-4" />
            Manage Blogs
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('clients')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'clients' 
                ? 'bg-[#4338CA] text-white' 
                : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
            }`}
            data-testid="tab-clients"
          >
            <Building2 className="w-5 h-5" />
            Client Management
          </button>
          <button
            onClick={() => setActiveTab('team')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'team' 
                ? 'bg-[#4338CA] text-white' 
                : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
            }`}
            data-testid="tab-team"
          >
            <UserCog className="w-5 h-5" />
            Team Management
          </button>
          <button
            onClick={() => setActiveTab('access')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'access'
                ? 'bg-[#4338CA] text-white'
                : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
            }`}
            data-testid="tab-access"
          >
            <KeyRound className="w-5 h-5" />
            Access Defaults
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-600">Loading...</div>
        ) : (
          <>
            {/* Client Management Tab */}
            {activeTab === 'clients' && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="clients-section">
                <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900" style={{fontFamily: 'Outfit'}}>
                      All Clients
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">{clients.length} total clients</p>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={fetchData}
                      className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg transition-colors"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Refresh
                    </button>
                    <button
                      onClick={() => setShowCreateClient(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-[#4338CA] text-white rounded-lg hover:bg-[#3730A3] transition-colors"
                      data-testid="create-client-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Create Client
                    </button>
                  </div>
                </div>

                {/* Create Client Modal */}
                {showCreateClient && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-full max-w-md" data-testid="create-client-modal">
                      <h4 className="text-lg font-semibold mb-4">Create New Client</h4>
                      <form onSubmit={handleCreateClient} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Firm Name</label>
                          <input
                            type="text"
                            value={newClient.name}
                            onChange={(e) => setNewClient({...newClient, name: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4338CA]"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                          <input
                            type="email"
                            value={newClient.email}
                            onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4338CA]"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                          <input
                            type="password"
                            value={newClient.password}
                            onChange={(e) => setNewClient({...newClient, password: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4338CA]"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Subscription Plan</label>
                          <select
                            value={newClient.subscription_plan}
                            onChange={(e) => setNewClient({...newClient, subscription_plan: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4338CA]"
                          >
                            <option value="Basic">Basic</option>
                            <option value="Growth">Growth</option>
                            <option value="Premium">Premium</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Assign Team Member</label>
                          <select
                            value={newClient.assigned_team_member_id}
                            onChange={(e) => setNewClient({...newClient, assigned_team_member_id: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4338CA]"
                          >
                            <option value="">-- Unassigned --</option>
                            {teamMembers.map(tm => (
                              <option key={tm.id} value={tm.id}>{tm.email}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex gap-3 pt-4">
                          <button
                            type="button"
                            onClick={() => setShowCreateClient(false)}
                            className="flex-1 px-4 py-2 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="flex-1 px-4 py-2 bg-[#4338CA] text-white rounded-lg hover:bg-[#3730A3]"
                          >
                            Create Client
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                {/* Clients Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">Client</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">Plan</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">Assigned To</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">Keywords</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">Created</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {clients.map((client) => (
                        <tr key={client.id} className="hover:bg-slate-50" data-testid={`client-row-${client.id}`}>
                          <td className="px-6 py-4">
                            <div className="font-medium text-slate-900">{client.firm_name}</div>
                            <div className="text-sm text-slate-500">{client.email}</div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 bg-[#4338CA]/10 text-[#4338CA] rounded-full text-xs font-medium">
                              {client.subscription_plan}
                            </span>
                            {client.plan_config?.plan_type && (
                              <span className={`ml-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border ${client.plan_config.plan_type === 'custom' ? 'bg-violet-100 text-violet-700 border-violet-300' : 'bg-slate-100 text-slate-600 border-slate-300'}`}>
                                {client.plan_config.plan_type}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <select
                              value={client.status || 'Active'}
                              onChange={(e) => handleUpdateClientStatus(client.id, e.target.value)}
                              className="px-2 py-1 text-sm border border-slate-200 rounded focus:outline-none"
                            >
                              <option value="Active">Active</option>
                              <option value="In Setup">In Setup</option>
                              <option value="Inactive">Inactive</option>
                            </select>
                          </td>
                          <td className="px-6 py-4">
                            <select
                              value={client.assigned_team_member?.id || ''}
                              onChange={(e) => handleAssignClient(client.firm_id, e.target.value)}
                              className="px-2 py-1 text-sm border border-slate-200 rounded focus:outline-none"
                            >
                              <option value="">-- Unassigned --</option>
                              {teamMembers.map(tm => (
                                <option key={tm.id} value={tm.id}>{tm.email}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-6 py-4 text-slate-600">{client.keywords_count}</td>
                          <td className="px-6 py-4 text-sm text-slate-500">
                            {client.created_at ? new Date(client.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => onViewClientDashboard(client.firm_id, client.firm_name)}
                                className="p-2 text-slate-400 hover:text-[#4338CA] hover:bg-[#4338CA]/10 rounded-lg transition-colors"
                                title="View Dashboard"
                                data-testid={`view-dashboard-${client.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setPlanClient(client)}
                                className="p-2 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                                title="Plan & Access Configuration"
                                data-testid={`plan-config-${client.id}`}
                              >
                                <Settings className="w-4 h-4" />
                              </button>
                              <button
                                onClick={async () => {
                                  if (!client.client_user_id) {
                                    alert('No primary client user is associated with this firm.');
                                    return;
                                  }
                                  try {
                                    const token = localStorage.getItem('token');
                                    const res = await axios.post(
                                      `${API}/admin/users/${client.client_user_id}/send-password-reset`,
                                      {},
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    alert(res.data.message || 'Password reset link sent successfully.');
                                  } catch (err) {
                                    alert(err.response?.data?.detail || 'Could not send the reset email - please try again.');
                                  }
                                }}
                                className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                title="Send Password Reset Link"
                                data-testid={`send-password-reset-${client.id}`}
                              >
                                <KeyRound className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteClient(client.id)}
                                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete Client"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {clients.length === 0 && (
                    <div className="text-center py-12 text-slate-500">No clients found</div>
                  )}
                </div>
              </div>
            )}

            {/* Team Management Tab */}
            {activeTab === 'team' && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="team-section">
                <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900" style={{fontFamily: 'Outfit'}}>
                      Internal Team Members
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">{teamMembers.length} team members</p>
                  </div>
                  <button
                    onClick={() => setShowCreateTeam(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-[#4338CA] text-white rounded-lg hover:bg-[#3730A3] transition-colors"
                    data-testid="create-team-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Add Team Member
                  </button>
                </div>

                {/* Create Team Member Modal */}
                {showCreateTeam && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-full max-w-md">
                      <h4 className="text-lg font-semibold mb-4">Add Team Member</h4>
                      <form onSubmit={handleCreateTeamMember} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                          <input
                            type="email"
                            value={newTeamMember.email}
                            onChange={(e) => setNewTeamMember({...newTeamMember, email: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4338CA]"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                          <input
                            type="password"
                            value={newTeamMember.password}
                            onChange={(e) => setNewTeamMember({...newTeamMember, password: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4338CA]"
                            required
                          />
                        </div>
                        <div className="flex gap-3 pt-4">
                          <button
                            type="button"
                            onClick={() => setShowCreateTeam(false)}
                            className="flex-1 px-4 py-2 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="flex-1 px-4 py-2 bg-[#4338CA] text-white rounded-lg hover:bg-[#3730A3]"
                          >
                            Add Member
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                {/* Team Members Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">Email</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">Role</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">Assigned Clients</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">Blog Access</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">Created</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {teamMembers.map((member) => (
                        <tr key={member.id} className="hover:bg-slate-50" data-testid={`team-row-${member.id}`}>
                          <td className="px-6 py-4">
                            <div className="font-medium text-slate-900">{member.email}</div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 bg-[#4338CA]/10 text-[#4338CA] rounded-full text-xs font-medium">
                              Internal Team
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              {member.status}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="text-slate-900 font-medium">{member.assigned_clients_count}</span>
                                <span className="text-slate-500">clients</span>
                              </div>
                              {member.assigned_clients && member.assigned_clients.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {member.assigned_clients.map((client, idx) => (
                                    <div key={idx} className="text-xs text-slate-500 flex items-center gap-2">
                                      <span className="w-1.5 h-1.5 bg-[#4338CA] rounded-full"></span>
                                      <span className="font-medium text-slate-700">{client.firm_name}</span>
                                      <span className="text-slate-400">({client.email})</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => handleToggleBlogAccess(member.id)}
                              disabled={togglingBlog === member.id}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                                member.can_manage_blogs ? 'bg-violet-600' : 'bg-slate-200'
                              } ${togglingBlog === member.id ? 'opacity-50' : ''}`}
                              data-testid={`toggle-blog-${member.id}`}
                              title={member.can_manage_blogs ? 'Blog access enabled' : 'Blog access disabled'}
                            >
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
                                member.can_manage_blogs ? 'translate-x-6' : 'translate-x-1'
                              }`} />
                            </button>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-500">
                            {member.created_at ? new Date(member.created_at).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {teamMembers.length === 0 && (
                    <div className="text-center py-12 text-slate-500">No team members found</div>
                  )}
                </div>

                {/* Super Admin Management */}
                <div className="border-t border-slate-200 mt-2">
                  <div className="p-6 flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 inline-flex items-center gap-2" style={{fontFamily: 'Outfit'}}>
                        <Shield className="w-4 h-4 text-violet-600" /> Super Admin Management
                      </h3>
                      <p className="text-sm text-slate-500 mt-1">Create another Super Admin user with full platform access.</p>
                    </div>
                    <button
                      onClick={() => setShowCreateAdmin(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
                      data-testid="create-super-admin-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Add Super Admin
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Access Defaults Tab (outside loading gate — self-fetches) */}
      {activeTab === 'access' && !loading && (
        <div className="max-w-7xl mx-auto px-8 -mt-2 pb-8">
          <AccessDefaultsAdmin />
        </div>
      )}

      {/* Plan & Access Modal */}
      {planClient && (
        <PlanConfigModal
          client={planClient}
          onClose={() => setPlanClient(null)}
          onSaved={fetchData}
        />
      )}

      {/* Create Super Admin Modal */}
      {showCreateAdmin && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateAdmin(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md" data-testid="create-super-admin-modal" onClick={(e) => e.stopPropagation()}>
            <h4 className="text-lg font-semibold mb-4 inline-flex items-center gap-2"><Shield className="w-5 h-5 text-violet-600" /> Create New Super Admin</h4>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  const token = localStorage.getItem('token');
                  await axios.post(`${API}/admin/users`, {
                    email: newAdmin.email,
                    password: newAdmin.password,
                    name: newAdmin.name,
                    role: 'super_admin'
                  }, { headers: { Authorization: `Bearer ${token}` } });
                  setShowCreateAdmin(false);
                  setNewAdmin({ name: '', email: '', password: '' });
                  fetchData();
                  alert('Super Admin created successfully.');
                } catch (err) {
                  alert(err.response?.data?.detail || 'Failed to create super admin');
                }
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Display Name</label>
                <input
                  type="text" value={newAdmin.name}
                  onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="new-admin-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  type="email" value={newAdmin.email}
                  onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  required data-testid="new-admin-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                <input
                  type="password" value={newAdmin.password}
                  onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  required minLength={6} data-testid="new-admin-password"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowCreateAdmin(false)} className="px-4 py-2 text-slate-600 hover:text-slate-900">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700" data-testid="new-admin-submit">Create Super Admin</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
